// Shared bookkeeping for a tiered (phase 1 → phase 2) schema-snapshot cache
// (SLICES.md § S7 items 1 + 2). `mssqlTableCache.ts` grew this shape first;
// item 8 gave the same one to `pgSchemaCache` / `mysqlSchemaCache` /
// `oracleSchemaCache` / `sqliteSchemaCache`, so the entry type, the publish
// throttle and the "wait until nothing is loading" accessor live here rather
// than four times over.

import { publish as publishSchemaBus } from '@/features/intellisense/schemaBus/schemaBus';
import type { SchemaSnapshot } from '@/core/engines/intellisenseTypes';
// Relative, not `@/`: the electron build (tsconfig.node.json, no alias)
// compiles this module too, through the engines' schema caches.
import { classifyDbError } from '../../../../core/analytics/dbErrorClassifier';
import type { AnalyticsErrorCategory } from '../../../../core/analytics/analyticsTypes';

import { clearIntrospectionFailures } from './introspectionTruncation';

/**
 * How much of the snapshot has been filled in. `'tables'` is a complete,
 * usable snapshot for relation-name completion whose per-relation payload is
 * still loading; `'complete'` means phase 2 finished (successfully, or with a
 * degraded artefact named in `feature:schema_snapshot_partial`).
 */
export type SnapshotPhase = 'tables' | 'complete';

export type TieredCacheEntry<TSnapshot> = {
  snapshot?: TSnapshot;
  lastError?: string;
  /** Phase-1 dedup — resolves as soon as the relations-only snapshot publishes. */
  refreshPromise?: Promise<TSnapshot>;
  /**
   * The database / schema `refreshPromise` is loading. The cache is keyed by
   * connection, so a request for a DIFFERENT scope must not be handed the
   * in-flight promise — it would resolve to the other scope's snapshot
   * (release review 1.5.0, F4).
   */
  refreshScope?: string;
  /**
   * `Date.now()` when the current snapshot finished loading with at least one
   * failed artefact (or no relation list at all). Such a snapshot is served —
   * stale beats empty — but only for `DEGRADED_RETRY_AFTER_MS`, not the
   * day-scale TTL, so a transient failure is retried soon without a refetch
   * on every keystroke (release review 1.5.0, F3). Cleared by a clean load.
   */
  degradedAt?: number;
  /** Phase-2 completion for the CURRENT generation. Never rejects. */
  phase2?: Promise<void>;
  /**
   * Column completion for the CURRENT generation — the phase-2 step that
   * fills in every relation's columns (with whatever that engine loads in the
   * same step: PostgreSQL enum labels, Oracle PK flags). Never rejects. Set
   * synchronously when phase 2 starts; an engine whose columns arrive with the
   * rest of phase 2 in one walk (SQLite) leaves it unset and
   * {@link whenEntryColumnsLoaded} falls back to `phase2`.
   */
  columnsLoaded?: Promise<void>;
  /**
   * Monotonic invalidation counter. Bumped by every refresh AND by
   * drop/invalidate, so a phase 2 (or a late phase-1 projection) whose
   * generation is stale stops merging and stops paging.
   */
  generation: number;
  /** `Date.now()` of the last bus publish, for the throttle below. */
  lastPublishAt: number;
};

export function createTieredCacheEntry<TSnapshot>(): TieredCacheEntry<TSnapshot> {
  return { generation: 0, lastPublishAt: 0 };
}

/**
 * What one load (phase 1 + phase 2) could not read: the artefact names that
 * become `missing` on `feature:schema_snapshot_partial`, and the FIRST
 * failure's error, which becomes its `category`. Created per load and threaded
 * through both phases, exactly like the plain `missing` list it replaces.
 */
export type LoadFailures = {
  readonly missing: string[];
  firstError?: unknown;
};

export function createLoadFailures(): LoadFailures {
  return { missing: [] };
}

/** Record that `artefact` failed to load with `error`; the first recorded error is kept. */
export function recordLoadFailure(failures: LoadFailures, artefact: string, error: unknown): void {
  failures.missing.push(artefact);
  if (failures.missing.length === 1) failures.firstError = error;
}

/** How long a degraded snapshot (see `degradedAt`) is served before `ensure…` retries the load. */
export const DEGRADED_RETRY_AFTER_MS = 60_000;

/**
 * Is `entry.snapshot` fresh enough to serve? A clean snapshot lives `ttlMs`
 * (by default the day-scale `SNAPSHOT_STALE_AFTER_MS`); a degraded one only
 * `DEGRADED_RETRY_AFTER_MS` — whichever is shorter — measured from when the
 * failure was recorded. An in-flight refresh keeps serving the snapshot as
 * is (the refresh dedup handles the retry), so degraded-ness never triggers a
 * second concurrent load.
 */
export function isEntryFresh<TSnapshot extends { builtAt: number }>(
  entry: TieredCacheEntry<TSnapshot>,
  ttlMs: number,
): boolean {
  const snapshot = entry.snapshot;
  if (!snapshot) return false;
  const now = Date.now();
  if (now - snapshot.builtAt >= ttlMs) return false;
  if (entry.degradedAt === undefined) return true;
  return now - entry.degradedAt < Math.min(ttlMs, DEGRADED_RETRY_AFTER_MS);
}

/**
 * Phase 1 could not read the relation list. The relation list is the
 * snapshot's spine: caching the relation-less `built` snapshot as a clean,
 * complete one would hide the failure for the day-scale TTL and every
 * completion would silently come up empty (release review 1.5.0, F3).
 * Instead serve the SAME-scope snapshot already held (re-stamped so the
 * degraded retry window, not a stale `builtAt`, governs the retry) — stale
 * beats empty — or, with nothing to fall back on, `built` marked complete;
 * either way the load is recorded as degraded, the partial-snapshot counter
 * fires, and `ensure…` retries after `DEGRADED_RETRY_AFTER_MS`. Phase 2 is
 * not started: there is nothing to enrich.
 */
export function publishDegraded<TSnapshot extends { builtAt: number; phase?: SnapshotPhase }>(
  entry: TieredCacheEntry<TSnapshot>,
  generation: number,
  args: {
    readonly previous: TSnapshot | undefined;
    readonly built: TSnapshot;
    readonly failures: LoadFailures;
    readonly engine: string;
    readonly project: (snapshot: TSnapshot) => SchemaSnapshot;
  },
): TSnapshot {
  const degraded: TSnapshot = args.previous
    ? { ...args.previous, builtAt: Date.now() }
    : { ...args.built, phase: 'complete' };
  if (entry.generation !== generation) return degraded;
  entry.snapshot = degraded;
  markLoadOutcome(entry, args.failures.missing);
  publishThrottled(entry, args.project(degraded), true);
  reportPartialSnapshot(args.engine, args.failures);
  return degraded;
}

/**
 * Record how a load ended for the freshness rule above: `missing` names the
 * artefacts phase 1 / phase 2 could not load (the same list that goes to
 * `feature:schema_snapshot_partial`). A no-generation-check convenience — the
 * caller has already verified it is still the current generation.
 */
export function markLoadOutcome<TSnapshot>(
  entry: TieredCacheEntry<TSnapshot>,
  missing: readonly string[],
  scope?: { readonly connectionId: string; readonly database: string },
): void {
  if (missing.length === 0) {
    entry.degradedAt = undefined;
    entry.lastError = undefined;
    // A clean load: any failure record on this scope is stale (release
    // review 1.5.0, R3) — a superseded load's late report, or a record the
    // begin-load clear could not know about.
    if (scope) clearIntrospectionFailures(scope.connectionId, scope.database);
    return;
  }
  entry.degradedAt = Date.now();
  entry.lastError = `Schema partially loaded: ${missing.slice().sort().join(', ')}`;
}

/**
 * How often the bus may be re-published while phase 2 streams in. Every page
 * merges immediately, but publishing each one would wake every bus subscriber
 * (editors, VQE contexts, the pill) dozens of times per second on a large
 * database. The final publish of each artefact is always forced, so the last
 * state is never left unpublished.
 */
export const REPUBLISH_MIN_INTERVAL_MS = 150;

/**
 * Publish `projected` to the schema bus, at most once per
 * `REPUBLISH_MIN_INTERVAL_MS` unless `force`. Publishes a SHALLOW COPY so
 * subscribers comparing snapshot identity see a change; the tables / FK /
 * routine arrays stay shared with the live snapshot on purpose — a consumer
 * holding an older publish keeps reading fresh data.
 */
export function publishThrottled<TSnapshot>(
  entry: TieredCacheEntry<TSnapshot>,
  projected: SchemaSnapshot,
  force: boolean,
): void {
  const now = Date.now();
  if (!force && now - entry.lastPublishAt < REPUBLISH_MIN_INTERVAL_MS) return;
  entry.lastPublishAt = now;
  publishSchemaBus({ ...projected });
}

/**
 * Resolve once nothing is loading for this entry — phase 1 published AND
 * phase 2 finished merging. Returns immediately when nothing is in flight
 * (including when no snapshot exists at all). Never rejects: "complete" here
 * means "no longer loading", not "succeeded".
 *
 * A refresh that starts while we await restarts the wait, bounded so a
 * pathological refresh loop cannot hang a caller forever.
 */
export async function whenEntryComplete<TSnapshot>(
  entry: TieredCacheEntry<TSnapshot> | undefined,
): Promise<void> {
  if (!entry) return;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const refresh = entry.refreshPromise;
    if (refresh) await refresh.catch(() => undefined);
    const phase2 = entry.phase2;
    if (phase2) await phase2.catch(() => undefined);
    if (!entry.refreshPromise) return;
  }
}

/**
 * Resolve once the entry's columns have loaded — phase 1 published AND the
 * current generation's column step finished — without waiting for the rest
 * of phase 2 (FK edges, CHECK clauses, indexes, routines, sequences, types).
 * This is the Visual Query Editor's readiness point: it edits tables and
 * columns, and picks the later artefacts up from the schema bus as they land.
 * Same contract as {@link whenEntryComplete}: returns at once when nothing is
 * loading, never rejects, and a refresh that starts while waiting restarts
 * the wait (bounded).
 */
export async function whenEntryColumnsLoaded(
  entry: Pick<TieredCacheEntry<unknown>, 'refreshPromise' | 'phase2' | 'columnsLoaded'> | undefined,
): Promise<void> {
  if (!entry) return;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const refresh = entry.refreshPromise;
    if (refresh) await refresh.catch(() => undefined);
    const columns = entry.columnsLoaded ?? entry.phase2;
    if (columns) await columns.catch(() => undefined);
    if (!entry.refreshPromise) return;
  }
}

/**
 * The `category` of a partial load: the SAME classifier `feature:query_error`
 * uses (`classifyDbError`), applied to the load's first failure — usually the
 * cause the others cascade from (a dropped connection fails every artefact
 * still queued behind it). A rejection that is not an Error, or carries no
 * recognisable text, classifies as `'unknown'`.
 */
function partialSnapshotCategory(engine: string, error: unknown): AnalyticsErrorCategory {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const code = (error as { code?: unknown } | null | undefined)?.code;
  const structuredCode = typeof code === 'string' || typeof code === 'number' ? code : undefined;
  return classifyDbError(engine, message, structuredCode).category;
}

/**
 * Report the artefacts a load could not read. Same low-cardinality
 * `feature:schema_snapshot_partial` counter every engine's phase 2 (and
 * degraded phase 1) fires; `missing` is sorted so the property value set stays
 * small. Kusto has no report site: its schema is ONE `.show schema` command,
 * loaded whole or not at all (`kustoSchemaLoad.ts`), so it is never partial.
 */
export function reportPartialSnapshot(engine: string, failures: LoadFailures): void {
  const { missing } = failures;
  if (missing.length === 0) return;
  if (typeof window === 'undefined') return;
  window.electronAPI?.analytics
    ?.track('feature:schema_snapshot_partial', {
      engine,
      missing: missing.slice().sort().join(','),
      category: partialSnapshotCategory(engine, failures.firstError),
    })
    .catch(() => {
      // Analytics must never affect UX.
    });
}
